import { graphql } from 'react-relay';
import memoizeOne, { type MemoizedFn } from 'memoize-one';

import { toDocumentNode } from '@confluence/apollo-relay-interop';
import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient.ts';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';
import { getCloudId } from '@confluence/session-data/entry-points/SessionData.ts';

// Fragments for reading cached page title from different GraphQL types
const PTPageTitleFragment = graphql`
	fragment getCachedPageTitle_PageTreeTitleFragment on PTPage {
		id
		title
	}
`;
const ContentTitleFragment = graphql`
	fragment getCachedPageTitle_ContentTitleFragment on Content {
		id
		title
	}
`;
const SearchConfluencePageBlogAttachmentTitleFragment = graphql`
	fragment getCachedPageTitle_SearchTitleFragment on SearchConfluencePageBlogAttachment {
		id
		title
	}
`;

export type CachedPageTitle = {
	title?: string;
	source?: string; // the GraphQL type/source the title was found from
};

enum Client {
	CC_GRAPHQL = 'Cc graphql client',
	AGG_GRAPHQL = 'Agg graphql client',
}

const FRAGMENTS_BY_TYPE: {
	client: Client;
	gqlType: string;
	fragment: ReturnType<typeof graphql>;
}[] = [
	{
		// Page tree stores pages as PTPage
		client: Client.CC_GRAPHQL,
		gqlType: 'PTPage',
		fragment: PTPageTitleFragment,
	},
	{
		// Current page, pages from Recents, Home's Discover feed, etc.
		client: Client.CC_GRAPHQL,
		gqlType: 'Content',
		fragment: ContentTitleFragment,
	},
	{
		// Search query uses AGG graphql client
		client: Client.AGG_GRAPHQL,
		gqlType: 'SearchConfluencePageBlogAttachment',
		fragment: SearchConfluencePageBlogAttachmentTitleFragment,
	},
];

/**
 * Retrieves the page title by contentId from Apollo cache by looking at types returned by multiple queries.
 *
 * This function checks multiple GraphQL types in the cache:
 * - PTPage: Pages from the Page Tree (most common for transitional loads)
 * - Content: Current page, pages from Recents (Worked on/Created by me), Home's Discover feed
 * - SearchConfluencePageBlogAttachment: Pages from search results
 *
 * Use case: During transitional loads from the page tree to the editor, the page title
 * should already be cached from the page tree query. This function allows us to use that
 * cached title immediately instead of waiting for the collab service to initialize.
 */
export const getCachedPageTitle: MemoizedFn<(contentId?: string) => CachedPageTitle> = memoizeOne(
	(contentId?: string) => {
		if (!contentId) {
			return { title: undefined, source: undefined };
		}

		const cloudId = getCloudId();
		const ccGqlClient = getApolloClient();
		const aggGqlClient = getAGGClient();

		const clientMap = {
			[Client.CC_GRAPHQL]: ccGqlClient,
			[Client.AGG_GRAPHQL]: aggGqlClient,
		};

		let title: string | undefined;
		let source: string | undefined;

		FRAGMENTS_BY_TYPE.some(({ client, gqlType, fragment }) => {
			// readFragment throws an error if it finds the item in the cache without both id and subtype, so we wrap in try/catch
			try {
				let id = `${gqlType}:${contentId}`;
				if (gqlType === 'SearchConfluencePageBlogAttachment') {
					// Search graphql query includes the ARI in the id
					id = `${gqlType}:ari:cloud:confluence:${cloudId}:page/${contentId}`;
				}

				// This approach is duplicated in /confluence/next/packages/live-pages-utils/src/getCachedPageData.ts
				// and should be migrated to readFragmentInterop.
				const fragmentData = clientMap[client].readFragment({
					id,
					fragment: toDocumentNode(fragment),
				});

				if (fragmentData?.title) {
					title = fragmentData.title;
					source = gqlType;
					return true;
				}
			} catch {
				// Fragment not found in cache, continue to next type
			}
			return false;
		});

		return { title, source };
	},
);
